<template>
  <div v-if="isLoading">
    <Spinner />
  </div>
  <div v-else>
    <Table>
      <TableHeader>
        <TableRow class="border-neutral-700">
          <TableHead>User</TableHead>
          <TableHead>Billable</TableHead>
          <TableHead class="text-right" />
        </TableRow>
      </TableHeader>
      <TableBody v-if="Object.keys(externalUsers).length">
        <TableRow
          v-for="(externalUser, name, index) in externalUsers"
          :key="index"
          class="border-neutral-700"
        >
          <TableCell class="font-medium">
            <div class="flex gap-3 items-center">
              <Avatar
                size="large"
                :rounded="true"
                :color="externalUser.user.color"
                :src="
                  externalUser.user.avatarUrl || externalUser.user.gravatarUrl
                "
                :alt="externalUser.user.shortName"
                :initials="externalUser.user.shortName"
              />
              <div>
                <div class="text-base font-semibold text-white">
                  {{ externalUser.user.name }}
                </div>
                <div>{{ externalUser.user.email }}</div>
              </div>
            </div>
          </TableCell>
          <TableCell class="font-medium">
            <Check v-if="externalUser.is_billable" />
          </TableCell>
          <TableCell class="text-right">
            <DropdownMenu>
              <DropdownMenuTrigger as="span">
                <IconButton variant="ghost" size="sm">
                  <MoreHorizontal />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem @click="openDialog(externalUser.user_id)"
                  >See access</DropdownMenuItem
                >
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_MEMBERSHIPS_DELETE]"
                >
                  <DropdownMenuItem
                    @click="openRemoveDialog(externalUser.user_id)"
                    >Remove</DropdownMenuItem
                  >
                </Protect>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody v-else>
        <TableRow class="border-neutral-700">
          <TableCell class="text-center" colspan="4">
            No members found
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    <ExternalUserAccessDialog
      v-model:open="accessDialogOpen"
      :external-user="dialogUser"
    />
    <AlertDialog v-model:open="removeDialogOpen">
      <AlertDialogContent v-if="dialogUser?.user">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        </AlertDialogHeader>
        <div class="px-7">
          <AlertDialogDescription>
            This action will remove
            <strong>{{ dialogUser.user.firstName }}</strong> from all associated
            projects
          </AlertDialogDescription>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel as-child>
            <Button variant="ghost" :disabled="isRemovePending">Cancel</Button>
          </AlertDialogCancel>
          <AlertDialogAction as-child>
            <Button
              variant="danger"
              :disabled="isRemovePending"
              :loading="isRemovePending"
              @click="onRemoveConfirm(dialogUser.user.id)"
            >
              Remove
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Protect from '@/hoc/Protect.vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { MoreHorizontal, Check } from 'lucide-vue-next';
import { Avatar } from '@ui/components/avatar';
import { Button } from '@ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/components/table';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@ui/components/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import { IconButton } from '@ui/components/icon-button';
import Spinner from '@ui/components/Spinner.vue';
import requests from '@vuex/requests.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';
import ExternalUserAccessDialog from './ExternalUserAccessDialog.vue';

const { isLoading, data: externalUsers } = useQuery(
  queries.workspace.externalUsers
);

const dialogUserId = ref(null);
const dialogUser = computed(() => {
  if (
    dialogUserId.value !== null &&
    externalUsers.value?.[dialogUserId.value]
  ) {
    return externalUsers.value[dialogUserId.value];
  }
  return null;
});

const accessDialogOpen = ref(false);
function openDialog(userId) {
  dialogUserId.value = userId;
  accessDialogOpen.value = true;
}

const removeDialogOpen = ref(false);
function openRemoveDialog(userId) {
  dialogUserId.value = userId;
  removeDialogOpen.value = true;
}

const queryClient = useQueryClient();
const store = useStore();

const { isPending: isRemovePending, mutate: removeExternalUser } = useMutation({
  mutationFn: async payload => {
    await requests.workspaces.externalUsers.remove(
      store.getters['workspace/getId'],
      payload
    );
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries(
      queries.workspace.externalUsers.queryKey
    );
  },
});

function onRemoveConfirm(userId) {
  removeExternalUser({ userId });
}
</script>
